import { useState, useCallback } from "react";
import {
  useCreatePermissionMutation,
  useUpdatePermissionMutation,
  useDeletePermissionMutation,
} from "../../../../hooks/queries/usePermissionQueries";
import { toastService } from "../../../../providers";

export const usePermissionHandlers = ({
  modalMode,
  selectedPermission,
  closeModal,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const { mutate: createPermission, isPending: isCreating } =
    useCreatePermissionMutation();

  const { mutate: updatePermission, isPending: isUpdating } =
    useUpdatePermissionMutation();

  const { mutate: deletePermission, isPending: isDeleting } =
    useDeletePermissionMutation();

  const handleSubmit = useCallback(
    async (values) => {
      const closeModalAndReset = () => {
        closeModal();
      };

      if (modalMode === "create") {
        createPermission(values, {
          onSuccess: () => {
            toastService.success("Permission created successfully!");
            closeModalAndReset();
          },
          onError: (_err) => {
            toastService.error("Failed to create permission");
          },
        });
      } else if (modalMode === "edit" && selectedPermission) {
        updatePermission(
          {
            id: selectedPermission.id,
            permissionData: values,
          },
          {
            onSuccess: () => {
              toastService.success("Permission updated successfully!");
              closeModalAndReset();
            },
            onError: (_err) => {
              toastService.error("Failed to update permission");
            },
          },
        );
      }
    },
    [
      modalMode,
      selectedPermission,
      createPermission,
      updatePermission,
      closeModal,
    ],
  );

  const handleDeletePermission = useCallback((permission) => {
    if (!permission?.id) return;
    setPendingDelete(permission);
    setConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!pendingDelete?.id) return;
    deletePermission(pendingDelete.id, {
      onSuccess: () => {
        toastService.success("Permission deleted");
      },
      onError: (err) => {
        toastService.error(err.message || "Failed to delete permission");
      },
    });
    setConfirmOpen(false);
    setPendingDelete(null);
  }, [pendingDelete, deletePermission]);

  const handleCancelDelete = useCallback(() => {
    setConfirmOpen(false);
    setPendingDelete(null);
  }, []);

  return {
    handleSubmit,
    handleDeletePermission,
    confirmOpen,
    pendingDelete,
    handleConfirmDelete,
    handleCancelDelete,
    isCreating,
    isUpdating,
    isDeleting,
  };
};

export default usePermissionHandlers;
